import { loadScript } from "@/functions/misc";
import { MediaPlayerStatus } from "@/models/frontend-only/MediaPlayerStatus";
import { defineStore } from "pinia";

declare interface MusicKitInstance {
  currentPlaybackTime: number;
  currentPlaybackDuration: number;
  playbackState: number;
  isAuthorized: boolean;
  removeEventListener: (event: string, callback: () => void) => void;
  addEventListener: (event: string, callback: () => void) => void;
  authorize: () => Promise<void>;
  unauthorize: () => Promise<void>;
  play: () => Promise<void>;
  pause: () => Promise<void>;
  stop: () => Promise<void>;
  seekToTime: (time: number) => Promise<void>;
}

declare const document: Document & { apple: MusicKitInstance };

export const useAppleMusicStore = defineStore("apple", () => {
  const toastStore = useToastStore();
  const api = useApiStore();
  const { t } = useI18n();
  const debug = useDebug();
  let instance: MusicKitInstance | null = null;
  const authorized = ref(false);

  const status = ref<MediaPlayerStatus>(MediaPlayerStatus.Unloaded);
  const currentTime = ref(0);
  const duration = ref(0);
  const connecting = ref(false);
  const error = ref("");

  let token = "";

  function onProgressUpdate() {
    if (!instance) {
      return;
    }

    duration.value = instance.currentPlaybackDuration;
    currentTime.value = instance.currentPlaybackTime;
  }
  function onPlaybackStateChange() {
    if (instance?.playbackState === MusicKit.PlaybackStates.ended) {
      status.value = MediaPlayerStatus.Ended;
    }

    if (instance?.playbackState === MusicKit.PlaybackStates.playing) {
      status.value = MediaPlayerStatus.Playing;
    }
  }

  function getStatusUpdates() {
    authorized.value = instance?.isAuthorized || false;
  }

  async function getLatestToken() {
    debug.log("apple script loaded");
    if (instance) {
      instance.removeEventListener("playbackTimeDidChange", onProgressUpdate);
      instance.removeEventListener(
        "playbackStateDidChange",
        onPlaybackStateChange,
      );
    }

    document.removeEventListener("musickitloaded", getLatestToken);

    try {
      token = await api.post("/GetAppleApiToken");
      debug.log("apple jwt token", token);

      document.apple = (await MusicKit.configure({
        developerToken: token,
        app: {
          name: "CCLI SongSelect",
          build: import.meta.env.VITE_BUILD_VERSION,
        },
      })) as unknown as MusicKitInstance;
      debug.log("instance created", document.apple);
      instance = document.apple;

      getStatusUpdates();
      instance?.addEventListener("playbackTimeDidChange", onProgressUpdate);
      instance?.addEventListener(
        "playbackStateDidChange",
        onPlaybackStateChange,
      );
      instance?.addEventListener(
        "authorizationStatusDidChange",
        getStatusUpdates,
      );
      debug.log("apple store loaded");
    } catch (err) {
      debug.log(err);
      error.value = "Error connecting to Apple Music";
      toastErrorAuth();
      throw err;
    }
  }

  async function init() {
    debug.log("apple init");
    await loadTokensFromUserMeta();
    debug.log("apple store loading");
    document.addEventListener("musickitloaded", getLatestToken);
    loadScript(
      "https://js-cdn.music.apple.com/musickit/v3/musickit.js",
      "apple-musickit-js",
    );
  }

  function instanceAvailable(throwErrorIfNotAvailable = true) {
    if (instance) {
      return true;
    }

    if (throwErrorIfNotAvailable) {
      status.value = MediaPlayerStatus.Error;
      error.value = "Error connecting to Apple Music";
      debug.log("Music instance not found");
      toastErrorAuth();
    }
    return false;
  }

  async function loadTokensFromUserMeta() {
    clearTokensFromUserMeta();
    const settings = await getFullSongIntegrationTokens();

    if (settings.service === "apple" && settings.authInfo) {
      const authInfo = settings.authInfo as {
        key: string;
        value: string;
      }[];
      authInfo.forEach((k) => {
        localStorage.setItem(k.key, k.value);
      });
    } else {
      saveTokensFromUserMeta();
    }
  }

  async function saveTokensFromUserMeta() {
    const fullPlaybackStore = useFullSongPlaybackStore();
    const keys = Object.keys(localStorage).filter((k) => /^music./.test(k));
    const keysAndValues = keys.map((k) => ({
      key: k,
      value: localStorage.getItem(k),
    }));
    debug.log(
      "keys and values",
      keysAndValues,
      JSON.stringify(keysAndValues).length,
    );
    fullPlaybackStore.saveSettings(keysAndValues, "apple");
  }

  async function clearTokensFromUserMeta() {
    const keys = Object.keys(localStorage).filter((k) => /^music./.test(k));
    keys.forEach((k) => localStorage.removeItem(k));
  }

  async function authorize() {
    if (!instanceAvailable() || !instance || instance.isAuthorized) {
      return;
    }

    connecting.value = true;

    try {
      await instance.authorize();
    } catch (err) {
      toastErrorAuth();
      connecting.value = false;
      console.error("error authorizing", err);
      throw err;
    }
    connecting.value = false;
    toastStore.successToast(t("SV.LIT_ConnectedToAppleMusic"));
    saveTokensFromUserMeta();
  }

  function unauthorize() {
    if (!instanceAvailable(false) || !instance || !instance.isAuthorized) {
      return;
    }
    pause();
    instance.unauthorize();
    toastStore.successToast(t("SV.LIT_AppleMusicDisconnected"));
    clearTokensFromUserMeta();
  }

  function toastErrorAuth() {
    toastStore.errorToast(t("SV.MES_AppleConnectionError"));
  }

  async function seekTo(time: number) {
    if (!instanceAvailable(false) || !instance) {
      return;
    }

    await instance.seekToTime(time);
  }

  async function playSong(id: string) {
    error.value = "";
    if (!instanceAvailable(false) || !instance || !instance.isAuthorized) {
      return;
    }

    status.value = MediaPlayerStatus.Loading;

    try {
      await (instance as any).setQueue({ song: id });
      await (instance as any).changeToMediaItem(id);
    } catch (err) {
      error.value = "Playback is not available for this song";
      toastStore.errorToast(t("SV.MES_SongNotAvailableForPlayback"));
      status.value = MediaPlayerStatus.Error;

      debug.log("error playing song", err);
      throw err;
    }

    status.value = MediaPlayerStatus.Playing;
  }

  async function play() {
    error.value = "";
    if (!instanceAvailable(false) || !instance) {
      return;
    }

    status.value = MediaPlayerStatus.Loading;
    try {
      await instance.play();
      status.value = MediaPlayerStatus.Playing;
    } catch (err) {
      error.value = "Playback is not available for this song";
      status.value = MediaPlayerStatus.Error;
      debug.log(err);
    }
  }

  function pause() {
    error.value = "";

    if (!instanceAvailable(false) || !instance) {
      return;
    }

    status.value = MediaPlayerStatus.Paused;
    if (currentTime.value) {
      return instance.pause();
    } else {
      return instance.stop();
    }
  }

  init();

  return {
    pause,
    play,
    playSong,
    authorize,
    unauthorize,
    authorized,
    status,
    seekTo,
    error,
    duration,
    connecting,
    instance,
    currentTime,
  };
});
